import React, { useState } from 'react';
import FastAPIClient from '../services/apiClient';
import { useLocation, useNavigate, Link } from 'react-router-dom';

// Instantiating outside the component prevents creating a new instance on every render
const apiClient = new FastAPIClient();

const VerificationCodePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const users_id = location.state?.users_id || null;

    const [formData, setFormData] = useState({ code: '' });
    const [loginError, setLoginError] = useState('');
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // 1. Update formData.code on input change
    const handleChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
        setFormData(prev => ({ ...prev, code: value }));
    };

    // 2. Validate & log on submit
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');

        // Validate
        if (!formData.code) {
            setLoginError('Verification code is required.');
            return;
        }
        if (!/^\d{6}$/.test(formData.code)) {
            setLoginError('Code must be a 6-digit number.');
            return;
        }

        setIsAuthenticating(true);
        setSuccessMessage('');

        try {
            // console.log(formData.code, users_id)
            const response = await apiClient.verifyCode(users_id, formData.code);
            setSuccessMessage('Verification successful! Redirecting to dashboard...');
            setTimeout(() => {
                navigate('/dashboard');
            }, 1500);
        } catch (error) {
            const responseData = error.response?.data.detail;
            if (responseData && Array.isArray(responseData)) {
                const missingErrors = responseData.filter(err => err.type == 'missing');
                if (missingErrors.length > 0) {
                    const missingFields = missingErrors.map (err => {
                        const fieldName = err.loc[err.loc.length - 1];
                        return `"${fieldName}"`;
                    })
                    const fieldsSentence = missingFields.join(', ');
                    setLoginError(`Configuration error: Missing required payload field(s): ${fieldsSentence}.`);
                    return;
                }
            }
            if (error.response?.status === 401 || error.response?.status === 400) {
                setLoginError('Invalid or expired verification code. Please check your messages.')
                return;
            }
            // console.error('Verification failed:', error.response?.data?.detail);
            setLoginError(error.response?.data?.detail || 'Unable to connect to server. Please check your internet connection.');
        } finally {
            setIsAuthenticating(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-xl transform transition-all duration-300 ease-in-out hover:scale-[1.005]">
                <div className="flex justify-center mb-6">
                    <img
                        src="/logo.png"
                        alt="School Logo"
                        className="h-20"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://placehold.co/80x80/000080/FFFFFF?text=Logo';
                        }}
                    />
                </div>

                <h2 className="text-3xl font-bold text-center mb-6 text-slate-800">
                    School Payments <span className="text-blue-900">Login</span>
                </h2>

                <form onSubmit={handleLogin} className="space-y-5">
                    <div>
                        <label htmlFor="code" className="block text-sm font-medium mb-1 text-gray-600">
                            OTP Verification Code
                        </label>
                        <input
                            type="text"
                            id="code"
                            inputMode="numeric"
                            pattern="\d{6}"
                            maxLength={6}
                            className="w-full p-3 border border-gray-300 rounded-md outline-none transition-all duration-200 focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                            placeholder="xxxxxx"
                            value={formData.code}
                            onChange={handleChange}
                            required
                            disabled={isAuthenticating}
                        />
                    </div>

                    {loginError && (
                        <p className="text-red-500 text-sm text-center animate-pulse">{loginError}</p>
                    )}
                    {successMessage && (
                        <p className="text-gmail-green text-sm text-center mb-4">{successMessage}</p>
                    )}
                    <button
                        type="submit"
                        className="w-full py-3 px-4 rounded-md font-semibold text-white bg-blue-900 hover:bg-blue-950 active:bg-slate-900 transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                        disabled={isAuthenticating || !formData.code || formData.code.length !== 6}
                    >
                        {isAuthenticating ? 'Verifying...' : 'Verify'}
                    </button>
                    <div className="text-center pt-2">
                        <span className="text-sm text-gray-500">No or expired OTP? </span>
                        <Link 
                            to="/login"
                            className="text-sm font-semibold text-green-900 hover:text-blue-950 hover:underline transition-colors duration-200"
                        >
                            Retry Login
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VerificationCodePage;